// apps/client/src/hooks/useSuccessionReadiness.ts
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface LenderMixEntry {
  lender: string;
  loanCount: number;
  trailCents: number;
  percentage: number;
}

interface AgeDistribution {
  bucket: string;
  count: number;
  percentage: number;
}

export type ChecklistItemStatus = 'automated' | 'manual_pending' | 'manual_completed';

export interface ChecklistItem {
  id: number;
  label: string;
  status: ChecklistItemStatus;
  weight: number;
  value: string | number | null;
  detail?: any;
}

export interface SuccessionReadinessData {
  loansSettledLast2Years: number;
  lenderMix: LenderMixEntry[];
  loanAgeDistribution: AgeDistribution[];
  /** Line of Trail, from the same snapshot table the dashboard reads. */
  activeClientsOnTrail: number;
  /** Distinct loans ever seen (active + discharged). */
  totalDatabase: number;
  /** Loans still on book (not discharged). */
  totalLoansUnderManagement: number;
  /** Loan book value from the dashboard snapshot; null when none exists yet. */
  loanBookCents?: number | null;
  /** Actual trail billed over the trailing 12 complete months. */
  actualTrailing12mCents?: number;
  /** The `YYYY-MM` the month-scoped figures are measured on. */
  latestCompleteMonth?: string | null;
  loansNowVs12MonthsAgo: {
    current: number;
    previous: number;
    growthPercent: number | null;
  };
  readinessScore: number;
  automatedItemsScore: number;
  manualItemsScore: number;
  checklist: ChecklistItem[];
}

interface UseSuccessionReadinessParams {
  companyId: string;
}

interface UseSuccessionReadinessReturn {
  data: SuccessionReadinessData | null;
  loading: boolean;
  error: string | null;
  generatedAt: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch Succession Readiness Report data
 * Calls getSuccessionReadinessReport Cloud Function (4 parallel BigQuery queries)
 */
export function useSuccessionReadiness({
  companyId,
}: UseSuccessionReadinessParams): UseSuccessionReadinessReturn {
  const [data, setData] = useState<SuccessionReadinessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);

  const fetchReport = async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[useSuccessionReadiness] Fetching report:', { companyId });

      const getReport = httpsCallable<
        { companyId: string },
        { data: SuccessionReadinessData; generatedAt: string }
      >(functions, 'getSuccessionReadinessReport');

      const result = await getReport({ companyId });

      console.log('[useSuccessionReadiness] Report received:', {
        readinessScore: result.data.data.readinessScore,
        checklistItems: result.data.data.checklist.length,
        activeLOT: result.data.data.activeClientsOnTrail,
      });

      setData(result.data.data);
      setGeneratedAt(result.data.generatedAt);
    } catch (err: any) {
      console.error('[useSuccessionReadiness] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch succession readiness report');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [companyId]);

  return {
    data,
    loading,
    error,
    generatedAt,
    refetch: fetchReport,
  };
}
